/**
 * IntentReviewModal — shows the user the structured intent that the LLM parsed
 * from their prompt, BEFORE expensive project generation runs. The user can
 * edit any field as chips, then confirms — and the generation is locked to
 * exactly what they confirmed. This is the core fix for prompt drift.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check, X, AlertCircle, Sparkles, Plus, Edit3, Loader2,
} from 'lucide-react';

const Chip = ({ children, onRemove, color = 'cyan', testId }) => {
  const palette = {
    cyan:    'bg-cyan-500/10 text-cyan-300 border-cyan-500/40',
    emerald: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40',
    amber:   'bg-amber-500/10 text-amber-300 border-amber-500/40',
    rose:    'bg-rose-500/10 text-rose-300 border-rose-500/40',
  }[color] || 'bg-cyan-500/10 text-cyan-300 border-cyan-500/40';
  return (
    <span
      data-testid={testId}
      className={`group inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${palette}`}
    >
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="rounded-full p-0.5 opacity-60 transition hover:bg-white/10 hover:opacity-100"
          aria-label="Remove"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
};

const ChipList = ({ items, setItems, color, placeholder, testIdPrefix }) => {
  const [draft, setDraft] = useState('');
  const addItem = () => {
    const v = draft.trim();
    if (!v) return;
    setItems([...(items || []), v]);
    setDraft('');
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {(items || []).map((it, idx) => (
        <Chip
          key={`${it}-${idx}`}
          color={color}
          testId={`${testIdPrefix}-chip-${idx}`}
          onRemove={() => setItems(items.filter((_, i) => i !== idx))}
        >
          {it}
        </Chip>
      ))}
      <div className="flex items-center gap-1">
        <input
          data-testid={`${testIdPrefix}-input`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              addItem();
            }
          }}
          placeholder={placeholder}
          className="w-44 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white outline-none placeholder:text-white/30 focus:border-cyan-400/60"
        />
        <button
          type="button"
          onClick={addItem}
          className="rounded-full border border-white/10 bg-white/5 p-1.5 text-white/60 transition hover:border-cyan-400/40 hover:text-cyan-300"
          aria-label="Add"
          data-testid={`${testIdPrefix}-add`}
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export default function IntentReviewModal({
  open,
  intent,
  loading,
  fallback,
  error,
  language = 'tr',
  onCancel,
  onConfirm,
}) {
  // Local editable state — initialized from incoming intent. Reinit on each open.
  const [editing, setEditing] = useState(false);
  const [summary, setSummary] = useState('');
  const [category, setCategory] = useState('');
  const [features, setFeatures] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [targetUsers, setTargetUsers] = useState('');
  const [environment, setEnvironment] = useState('');
  const [sizeHint, setSizeHint] = useState('unspecified');
  const [exactDimensions, setExactDimensions] = useState('unspecified');
  const [weightHint, setWeightHint] = useState('unspecified');
  const [formFactor, setFormFactor] = useState('unspecified');

  useEffect(() => {
    if (!intent) return;
    setSummary(intent.inferred_summary || '');
    setCategory(intent.product_category || '');
    setFeatures(intent.must_have_features || []);
    setMaterials(intent.primary_materials || []);
    setExclusions(intent.exclusions || []);
    setTargetUsers(intent.target_users || '');
    setEnvironment(intent.operating_environment || '');
    const kd = intent.key_dimensions || {};
    setSizeHint(kd.size_hint || 'unspecified');
    setExactDimensions(kd.exact_dimensions || 'unspecified');
    setWeightHint(kd.weight_hint || 'unspecified');
    setFormFactor(kd.form_factor || 'unspecified');
    setEditing(false);
  }, [intent]);

  const T = useMemo(() => (language === 'tr'
    ? {
        title: 'AI Tasarımınızı Şöyle Anladı',
        sub: 'Üretime başlamadan önce onaylayın. Yanlış anlaşılan bir şey varsa düzeltin — tasarım buna kilitlenecek.',
        loading: 'İsteğiniz analiz ediliyor…',
        summary: 'Özet',
        category: 'Ürün kategorisi',
        features: 'Olmazsa olmaz özellikler',
        materials: 'Tercih edilen malzemeler',
        exclusions: 'İstenmeyenler',
        users: 'Hedef kullanıcılar',
        env: 'Çalışma ortamı',
        size: 'Boyut sınıfı',
        exactDimensions: 'Kesin ölçüler',
        weight: 'Ağırlık',
        form: 'Form faktörü',
        confidence: 'Güven',
        clarify: 'Daha net olması için',
        edit: 'Düzenle',
        done: 'Bitir',
        cancel: 'Vazgeç',
        confirm: 'Bu tasarımı üret',
        fallback: 'AI servisi şu an dolu. Aşağıdaki özetle devam edebilir veya elle düzenleyip onaylayabilirsiniz.',
        addFeature: 'Özellik ekle',
        addMaterial: 'Malzeme ekle',
        addExclusion: 'İstenmeyen ekle',
        sizes: { unspecified: 'Belirsiz', portable: 'Taşınabilir', handheld: 'El boyu', tabletop: 'Masaüstü', 'room-scale': 'Oda', 'vehicle-scale': 'Araç' },
      }
    : {
        title: 'Here\'s What the AI Heard',
        sub: 'Confirm this before we generate. If anything is wrong, edit it now — the design will be locked to your confirmation.',
        loading: 'Analyzing your request…',
        summary: 'Summary',
        category: 'Product category',
        features: 'Must-have features',
        materials: 'Preferred materials',
        exclusions: 'Exclusions',
        users: 'Target users',
        env: 'Operating environment',
        size: 'Size class',
        exactDimensions: 'Exact dimensions',
        weight: 'Weight',
        form: 'Form factor',
        confidence: 'Confidence',
        clarify: 'For more accuracy',
        edit: 'Edit',
        done: 'Done',
        cancel: 'Cancel',
        confirm: 'Generate this design',
        fallback: 'AI service is currently busy. You can proceed with this summary or edit fields manually before confirming.',
        addFeature: 'Add feature',
        addMaterial: 'Add material',
        addExclusion: 'Add exclusion',
        sizes: { unspecified: 'Unspecified', portable: 'Portable', handheld: 'Handheld', tabletop: 'Tabletop', 'room-scale': 'Room-scale', 'vehicle-scale': 'Vehicle-scale' },
      }
  ), [language]);

  const confidencePct = Math.round((intent?.confidence ?? 0) * 100);
  const confidenceColor =
    confidencePct >= 75 ? 'bg-emerald-500' :
    confidencePct >= 45 ? 'bg-cyan-500'    :
    confidencePct >  0   ? 'bg-amber-500'   : 'bg-rose-500';

  const handleConfirm = () => {
    onConfirm({
      product_category: category.trim() || intent?.product_category || '',
      product_subtype: intent?.product_subtype || '',
      must_have_features: features.filter(Boolean),
      key_dimensions: {
        size_hint: sizeHint,
        exact_dimensions: exactDimensions,
        weight_hint: weightHint,
        form_factor: formFactor,
      },
      primary_materials: materials.filter(Boolean),
      target_users: targetUsers.trim(),
      operating_environment: environment.trim(),
      exclusions: exclusions.filter(Boolean),
      inferred_summary: summary.trim() || intent?.inferred_summary || '',
      confidence: intent?.confidence ?? 0.5,
      clarification_needed: false,
      clarification_questions: [],
      original_prompt: intent?.original_prompt || '',
    });
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="intent-review-modal"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      >
        <motion.div
          initial={{ y: 24, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 16, opacity: 0 }}
          transition={{ type: 'spring', damping: 22, stiffness: 220 }}
          className="w-full max-w-3xl overflow-hidden rounded-2xl border border-cyan-500/30 bg-[#060B1A] shadow-[0_0_60px_-15px_rgba(34,211,238,0.6)]"
        >
          {/* Header */}
          <div className="relative border-b border-white/10 bg-gradient-to-br from-cyan-500/10 via-transparent to-emerald-500/5 px-6 py-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-2">
                  <Sparkles className="h-5 w-5 text-cyan-300" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white" data-testid="intent-modal-title">{T.title}</h2>
                  <p className="mt-1 text-xs text-white/60">{T.sub}</p>
                </div>
              </div>
              <button
                onClick={onCancel}
                className="rounded-lg p-1 text-white/40 transition hover:bg-white/5 hover:text-white"
                data-testid="intent-modal-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
            {loading && (
              <div className="flex flex-col items-center justify-center py-16 text-white/60" data-testid="intent-loading">
                <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
                <p className="mt-4 text-sm">{T.loading}</p>
              </div>
            )}

            {!loading && intent && (
              <>
                {fallback && (
                  <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200" data-testid="intent-fallback-banner">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{T.fallback}</span>
                  </div>
                )}

                {/* Inferred summary — the big quote */}
                <div className="mb-5 rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-cyan-300">{T.summary}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-white/40">{T.confidence}</span>
                      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-white/10">
                        <div className={`h-full ${confidenceColor}`} style={{ width: `${confidencePct}%` }} />
                      </div>
                      <span className="text-[10px] text-white/60">{confidencePct}%</span>
                    </div>
                  </div>
                  {editing ? (
                    <textarea
                      data-testid="intent-summary-input"
                      value={summary}
                      onChange={(e) => setSummary(e.target.value)}
                      rows={2}
                      className="w-full resize-none rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60"
                    />
                  ) : (
                    <p className="text-base font-medium text-white" data-testid="intent-summary-display">
                      "{summary || intent.inferred_summary}"
                    </p>
                  )}
                </div>

                {/* Clarification banner */}
                {intent.clarification_needed && intent.clarification_questions?.length > 0 && (
                  <div className="mb-5 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="intent-clarification">
                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-amber-300">
                      <AlertCircle className="h-3.5 w-3.5" /> {T.clarify}:
                    </div>
                    <ul className="space-y-1 text-xs text-amber-100/80">
                      {intent.clarification_questions.map((q, i) => (
                        <li key={i}>• {q}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Grid: Category + Features */}
                <div className="grid gap-5 md:grid-cols-2">
                  <Field label={T.category}>
                    {editing ? (
                      <input
                        data-testid="intent-category-input"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60"
                      />
                    ) : (
                      <Chip color="cyan" testId="intent-category-chip">{category}</Chip>
                    )}
                  </Field>

                  <Field label={T.users}>
                    {editing ? (
                      <input
                        data-testid="intent-users-input"
                        value={targetUsers}
                        onChange={(e) => setTargetUsers(e.target.value)}
                        placeholder="—"
                        className="w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60"
                      />
                    ) : targetUsers ? (
                      <Chip color="emerald" testId="intent-users-chip">{targetUsers}</Chip>
                    ) : (
                      <span className="text-xs text-white/30">—</span>
                    )}
                  </Field>
                </div>

                <Field label={T.features}>
                  <ChipList
                    items={features}
                    setItems={setFeatures}
                    color="emerald"
                    placeholder={T.addFeature}
                    testIdPrefix="intent-feature"
                  />
                </Field>

                <Field label={T.materials}>
                  <ChipList
                    items={materials}
                    setItems={setMaterials}
                    color="cyan"
                    placeholder={T.addMaterial}
                    testIdPrefix="intent-material"
                  />
                </Field>

                <Field label={T.exclusions}>
                  <ChipList
                    items={exclusions}
                    setItems={setExclusions}
                    color="rose"
                    placeholder={T.addExclusion}
                    testIdPrefix="intent-exclusion"
                  />
                </Field>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <Field label={T.size}>
                    <Selector value={sizeHint} setValue={setSizeHint} options={Object.keys(T.sizes)} labels={T.sizes} testId="intent-size" />
                  </Field>
                  <Field label={T.exactDimensions}>
                    <input
                      data-testid="intent-exact-dimensions-input"
                      value={exactDimensions === 'unspecified' ? '' : exactDimensions}
                      onChange={(e) => setExactDimensions(e.target.value || 'unspecified')}
                      placeholder="650 L × 450 W × 900 H mm"
                      className="w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60"
                    />
                  </Field>
                  <Field label={T.weight}>
                    <input
                      data-testid="intent-weight-input"
                      value={weightHint === 'unspecified' ? '' : weightHint}
                      onChange={(e) => setWeightHint(e.target.value || 'unspecified')}
                      placeholder="unspecified"
                      className="w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60"
                    />
                  </Field>
                  <Field label={T.form}>
                    <input
                      data-testid="intent-form-input"
                      value={formFactor === 'unspecified' ? '' : formFactor}
                      onChange={(e) => setFormFactor(e.target.value || 'unspecified')}
                      placeholder="unspecified"
                      className="w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60"
                    />
                  </Field>
                </div>

                <Field label={T.env}>
                  {editing ? (
                    <input
                      data-testid="intent-env-input"
                      value={environment}
                      onChange={(e) => setEnvironment(e.target.value)}
                      placeholder="—"
                      className="w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60"
                    />
                  ) : environment ? (
                    <Chip color="cyan" testId="intent-env-chip">{environment}</Chip>
                  ) : (
                    <span className="text-xs text-white/30">—</span>
                  )}
                </Field>
              </>
            )}
          </div>

          {/* Footer */}
          {!loading && intent && (
            <div className="flex items-center justify-between gap-3 border-t border-white/10 bg-black/30 px-6 py-4">
              <button
                onClick={() => setEditing(!editing)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs font-medium text-white/70 transition hover:border-cyan-400/40 hover:text-cyan-300"
                data-testid="intent-toggle-edit"
              >
                <Edit3 className="h-3.5 w-3.5" />
                {editing ? T.done : T.edit}
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={onCancel}
                  className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/70 transition hover:border-white/30 hover:text-white"
                  data-testid="intent-cancel-btn"
                >
                  {T.cancel}
                </button>
                <button
                  onClick={handleConfirm}
                  className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-cyan-500 to-emerald-500 px-5 py-2 text-sm font-semibold text-[#060B1A] shadow-lg transition hover:shadow-cyan-500/30"
                  data-testid="intent-confirm-btn"
                >
                  <Check className="h-4 w-4" />
                  {T.confirm}
                </button>
              </div>
            </div>
          )}

          {error && !intent && !loading && (
            <div className="border-t border-rose-500/30 bg-rose-500/5 px-6 py-4 text-sm text-rose-300" data-testid="intent-error">
              {error}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

const Field = ({ label, children }) => (
  <div className="mb-4">
    <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/40">{label}</div>
    <div>{children}</div>
  </div>
);

const Selector = ({ value, setValue, options, labels, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {options.map((opt) => {
      const active = value === opt;
      return (
        <button
          key={opt}
          type="button"
          onClick={() => setValue(opt)}
          data-testid={`${testId}-${opt}`}
          className={`rounded-full border px-2.5 py-1 text-[11px] transition ${
            active
              ? 'border-cyan-400/60 bg-cyan-500/15 text-cyan-200'
              : 'border-white/10 bg-white/5 text-white/50 hover:border-white/30 hover:text-white/80'
          }`}
        >
          {labels?.[opt] || opt}
        </button>
      );
    })}
  </div>
);
